import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Pencil, Palette, Trash2 } from 'lucide-react';

const COLOR_PALETTE = [
  { hex: '#EFF6FF', label: 'Blue' },
  { hex: '#FFF7ED', label: 'Orange' },
  { hex: '#F0FDF4', label: 'Green' },
  { hex: '#FEF9C3', label: 'Yellow' },
  { hex: '#FDF4FF', label: 'Purple' },
  { hex: '#FFF1F2', label: 'Red' },
  { hex: '#F0FDFA', label: 'Teal' },
  { hex: '#FFF5F7', label: 'Pink' },
  { hex: '#F8FAFC', label: 'Gray' },
  { hex: '#FFFBEB', label: 'Amber' },
];

const ColumnActionsMenu = ({
  column,
  firstNonAiColumnName,
  onRename,
  onChangeColor,
  onDelete,
}) => {
  const [open, setOpen] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(column.name);
  const menuRef = useRef(null);
  const renameRef = useRef(null);

  const isLocked = column.is_ai_suggestions;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
        setShowColorPicker(false);
        setShowDeleteConfirm(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (renaming) {
      setRenameValue(column.name);
      setTimeout(() => renameRef.current && renameRef.current.focus(), 50);
    }
  }, [renaming, column.name]);

  const handleRenameConfirm = () => {
    const name = renameValue.trim();
    if (name && name !== column.name) onRename(name);
    setRenaming(false);
    setOpen(false);
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') handleRenameConfirm();
    if (e.key === 'Escape') { setRenaming(false); setOpen(false); }
  };

  if (renaming) {
    return (
      <input
        ref={renameRef}
        type="text"
        value={renameValue}
        onChange={(e) => setRenameValue(e.target.value)}
        onKeyDown={handleRenameKeyDown}
        onBlur={handleRenameConfirm}
        className="px-2 py-0.5 text-sm border border-blue-400 rounded focus:outline-none w-28 bg-white"
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <div ref={menuRef} className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => { setOpen(!open); setShowColorPicker(false); setShowDeleteConfirm(false); }}
        className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-black hover:bg-opacity-10 transition-opacity"
      >
        <MoreVertical size={14} />
      </button>

      {open && (
        <div className="absolute top-6 right-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px]">
          {!isLocked && (
            <button
              onClick={() => { setOpen(false); setRenaming(true); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Pencil size={13} />
              Rename
            </button>
          )}

          <button
            onClick={() => { setShowColorPicker(!showColorPicker); setShowDeleteConfirm(false); }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Palette size={13} />
            Change color
          </button>

          {showColorPicker && (
            <div className="px-3 py-2 border-t border-gray-100">
              <div className="grid grid-cols-5 gap-1">
                {COLOR_PALETTE.map((c) => (
                  <button
                    key={c.hex}
                    title={c.label}
                    onClick={() => { onChangeColor(c.hex); setOpen(false); setShowColorPicker(false); }}
                    className="w-5 h-5 rounded border border-gray-300 hover:scale-125 transition-transform"
                    style={{ backgroundColor: c.hex }}
                  />
                ))}
              </div>
            </div>
          )}

          {!isLocked && (
            <>
              <div className="border-t border-gray-100 mt-1" />
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={13} />
                  Delete
                </button>
              ) : (
                <div className="px-3 py-2">
                  <p className="text-xs text-gray-600 mb-2">
                    Tasks will move to &ldquo;{firstNonAiColumnName}&rdquo;. Continue?
                  </p>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => { onDelete(); setOpen(false); }}
                      className="flex-1 px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ColumnActionsMenu;
